import { Box, Flex, Text } from "rebass";
import styled, { keyframes } from "styled-components";
import { useState, useEffect, KeyboardEvent } from "react";
import { Textarea } from "@rebass/forms";

import { EmojiSelector } from "MVVM/Components/EmoijSelector";
import theme from "theme/themeThree";

interface IEditableBioProps {
  bioText: string;
  onSave: (newBio: string) => Promise<void>;
  isEditable?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  maxLength?: number;
  showEmojiSelector?: boolean;
  autoSave?: boolean;
}

export const EditableBio = ({
  bioText: initialBioText,
  onSave,
  isEditable = true,
  isLoading = false,
  placeholder = "Add a short bio...",
  maxLength = 500,
  showEmojiSelector = true,
  autoSave = false,
}: IEditableBioProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [bioText, setBioText] = useState(initialBioText || "");
  const [displayBio, setDisplayBio] = useState(initialBioText || "");

  useEffect(() => {
    setBioText(initialBioText || "");
    setDisplayBio(initialBioText || "");
  }, [initialBioText]);

  const handleSave = async () => {
    if (isLoading) return;

    try {
      await onSave(bioText);
      setDisplayBio(bioText);
      setIsEditing(false);
    } catch (error) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setBioText(displayBio || "");
    setIsEditing(false);
  };

  const handleBioClick = () => {
    if (isEditable) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setBioText((prev) => prev + emoji);
  };

  const handleBioChange = async (newBio: string) => {
    setBioText(newBio);
    if (autoSave) {
      await onSave(newBio);
    }
  };

  const isSaveDisabled =
    isLoading ||
    bioText.trim() === displayBio ||
    bioText.trim() === "" ||
    bioText.length > maxLength;

  if (!displayBio && !isEditable) return null;

  return (
    <Box>
      {isEditing ? (
        <EditingContainer>
          <BioTextArea
            style={{
              border: "none",
              color: theme.colors.shade300,
              fontWeight: 500,
              lineHeight: "24px",
              padding: "12px",
            }}
            value={bioText}
            onChange={(e) => handleBioChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            autoFocus
          />
          <EditingFooter>
            <Flex alignItems="center">
              <CharacterCount>
                {bioText.length}/{maxLength}
              </CharacterCount>
              {showEmojiSelector && (
                <EmojiContainer ml={2}>
                  <EmojiSelector
                    onEmojiSelect={handleEmojiSelect}
                    position="right"
                  />
                </EmojiContainer>
              )}
            </Flex>
            {!autoSave && (
              <ButtonContainer>
                <CancelButton onClick={handleCancel} disabled={isLoading}>
                  Cancel
                </CancelButton>
                <SaveButton onClick={handleSave} disabled={isSaveDisabled}>
                  {isLoading ? "Saving..." : "Save"}
                </SaveButton>
              </ButtonContainer>
            )}
          </EditingFooter>
        </EditingContainer>
      ) : (
        <BioText $isClickable={isEditable} onClick={handleBioClick}>
          {displayBio || (isEditable ? placeholder : "")}
        </BioText>
      )}
    </Box>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EditingContainer = styled(Box)`
  animation: ${fadeIn} 0.2s ease-in-out;
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  position: relative;
`;

const EditingFooter = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: 0 0 8px 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  justify-content: space-between;
  padding: 12px;
  padding-top: 8px;
`;

const EmojiContainer = styled(Box)`
  align-items: center;
  display: flex;
`;

const BioTextArea = styled(Textarea)`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.shade200};
  font-family: Barlow;
  font-size: 16px;
  font-weight: 400;
  min-height: 80px;
  outline: none;
  resize: none;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.shade400};
  }
`;

const CharacterCount = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-size: 14px;
`;

const ButtonContainer = styled(Flex)`
  gap: 12px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  background: ${({ theme }) => theme.colors.navy50};
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;

  &:hover {
    background: ${({ theme }) => theme.colors.envyDark};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const BioText = styled(Box)<{ $isClickable: boolean }>`
  color: ${({ theme }) => theme.colors.shade300};
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  font-family: Barlow;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.1s ease-in-out;
  white-space: pre-line;

  ${({ $isClickable, theme }) =>
    $isClickable &&
    `{
      background: ${theme.colors.navy300};
      border: 1px solid ${theme.colors.navy50};
      border-radius: 8px;
      color: ${theme.colors.shade300};
      padding: 12px 16px;
    }
    &:hover {
      background: ${theme.colors.navy};
      border-color: ${theme.colors.envy};
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
  `}
`;
